import { useTranslation } from 'react-i18next';
import { AlertTriangle } from 'lucide-react';
import { cn } from '../../lib/utils';
import type { CragReport } from '../../types/crag-report';

interface CragReportsChipProps {
  /** The crag's own reports — the ones that belong to no line. */
  reports: CragReport[];
  onClick: () => void;
  className?: string;
}

/**
 * What is wrong with the crag itself, above the tabs.
 *
 * These are the reports no route can carry — the parking, the path, a nesting
 * ban on the whole cliff — so there is no row to hang a triangle off. They sit
 * beside the followers because that strip is the last thing read before the
 * tabs, which is exactly where "the sector is closed this spring" has to be
 * caught: after it, the reader is inside one sector and never sees the crag
 * again.
 *
 * A single report shows what it says; several collapse to a count, because
 * three paragraphs stacked above the tabs would push the crag off the screen.
 */
export function CragReportsChip({ reports, onClick, className }: CragReportsChipProps) {
  const { t } = useTranslation();

  const open = reports.filter((report) => report.status === 'accepted');
  if (open.length === 0) return null;

  const single = open.length === 1 ? open[0] : null;

  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="crag-reports-chip"
      className={cn(
        'flex min-w-0 items-center gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1.5',
        'text-left text-xs font-medium text-amber-700 transition-colors hover:bg-amber-500/20 dark:text-amber-400',
        className
      )}
    >
      <AlertTriangle className="h-4 w-4 shrink-0" />
      <span className="truncate">
        {single
          ? `${t(`cragReports.categories.${single.category}`)} · ${single.description}`
          : t('cragReports.openCount', { count: open.length })}
      </span>
    </button>
  );
}
